import React, {useState} from 'react';

import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/Util';
import {validateHELMQuery} from 'molbio/monomers/MonomersDAO';
import ErrorMessage from 'ui-library/ErrorMessage';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import insertTextFromPaste from 'util/insertTextFromPaste';
import pickDefined from 'util/pickDefined';
import {InputChangeEvent} from 'util/Types';

import {Filter, OligoHELMFilterDNA, OligoHELMFilterRNA, Operator} from '../SearchConstants';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import OperatorDropdown from './base/OperatorDropdown';
import SecondaryFilterFormSkeleton from './base/SecondaryFilterFormSkeleton';
import withDebouncing from './base/withDebouncing';

async function validate(
  value: string,
  operator: Operator,
  nucleotideType: NucleotideType,
  setError: (error: string) => void
): Promise<void> {
  if (value.length === 0) {
    setError('');
    return;
  }
  const res = await validateHELMQuery(value, operator, nucleotideType);
  if (res.error.length > 0) {
    setError(res.error);
  } else {
    setError('');
  }
}

interface HELMInputProps {
  error: string;
  onChangeValue: (value: string) => void;
  value: string;
}
function HELMInput({onChangeValue, error, value}: HELMInputProps): JSX.Element {
  return (
    <>
      <TextInput
        hasError={error.length > 0}
        onChange={(e: InputChangeEvent) => {
          onChangeValue(e.target.value);
        }}
        onPaste={insertTextFromPaste}
        size={Size.SMALL}
        value={value}
      />
      {error.length > 0 && <ErrorMessage error={error} />}
    </>
  );
}
const DebouncedHELMInput = withDebouncing(HELMInput);

export interface OligoHELMFilterFormProps extends BaseFilterFormProps {
  filter: OligoHELMFilterDNA | OligoHELMFilterRNA;
  filters: Array<Filter>;
  filterStyle: FilterStyle;
  nucleotideType: NucleotideType;
  onChangeFilter: (filter: OligoHELMFilterDNA | OligoHELMFilterRNA) => void;
  onRemove: () => void;
  operators: Array<Operator>;
}
export function OligoHELMFilterForm(props: OligoHELMFilterFormProps): JSX.Element {
  assert(props.filterStyle === FilterStyle.SECONDARY, 'HELM filter only supported as SECONDARY');
  const [error, setError] = useState('');

  const operatorDropdown = (
    <OperatorDropdown
      buttonSize={props.buttonSize}
      onChangeOperator={(operator: Operator) => {
        validate(props.filter.value, operator, props.nucleotideType, setError);
        props.onChangeFilter({
          ...props.filter,
          value: props.filter.value ?? '',
          operator,
        });
      }}
      operators={props.operators}
      selectedOperator={props.filter.operator}
    />
  );
  const helmInput = (
    <DebouncedHELMInput
      onChangeValue={(value: string) => {
        validate(value, props.filter.operator, props.nucleotideType, setError);
        props.onChangeFilter({
          key: props.filter.key,
          operator: props.filter.operator,
          value,
        });
      }}
      error={error}
      initialValue={props.filter.value}
    />
  );
  return (
    <SecondaryFilterFormSkeleton
      {...pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES)}
      nodeGrid={[[operatorDropdown], [helmInput]]}
      onChangeFilterKey={props.onChangeFilterKey}
      selectedFilterKey={props.selectedFilterKey}
      onRemove={props.onRemove}
    />
  );
}

export function OligoHELMFilterFormRNA(props: Omit<OligoHELMFilterFormProps, 'nucleotideType'>): JSX.Element {
  return <OligoHELMFilterForm {...props} nucleotideType={NucleotideType.RNA} />;
}

export function OligoHELMFilterFormDNA(props: Omit<OligoHELMFilterFormProps, 'nucleotideType'>): JSX.Element {
  return <OligoHELMFilterForm {...props} nucleotideType={NucleotideType.DNA} />;
}
